/**
 * SimpleVariantPicker — custom element for the "simple_variant_picker" block.
 *
 * Layout:
 *   Option 1 → image cards (one per unique option1 value)
 *   Option 2 → text buttons, filtered to the current option1 selection
 *   Description → richtext metafield (custom.variant_description), per variant
 *
 * Reuses the same form-update / section-fetch / media-update pattern as
 * custom-variant-picker.js so it works alongside Accessories & Filaments.
 */

if (!customElements.get('simple-variant-picker')) {
  class SimpleVariantPicker extends HTMLElement {

    // ── Boot ───────────────────────────────────────────────────────────────

    connectedCallback() {
      const jsonEl = this.querySelector('[data-svp-json]');
      if (!jsonEl) return;

      const data          = JSON.parse(jsonEl.textContent);
      this._variants      = data.variants;
      this._hasOpt2       = data.has_opt2;
      this._sectionId     = this.dataset.section;
      this._productUrl    = this.dataset.url;

      // Media references (mirrors custom-variant-picker.js)
      this._productWrapper = this.closest('.thb-product-detail');
      if (this._productWrapper) {
        this._productSlider =
          this._productWrapper.querySelector('.product-images') ||
          this._productWrapper.querySelector('.product-quick-images');
        this._thumbnails = this._productWrapper.querySelector('.product-thumbnail-container');
      }

      this._bindEvents();
    }

    // ── Events ─────────────────────────────────────────────────────────────

    _bindEvents() {
      // Option 1 card clicks
      this.querySelectorAll('.svp__card').forEach((card) => {
        card.addEventListener('click', () => this._onCardClick(card));
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._onCardClick(card); }
        });
      });

      // Option 2 button clicks
      this.querySelectorAll('.svp__opt2-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._onOpt2Click(btn);
        });
      });
    }

    // ── Option 1 card click ────────────────────────────────────────────────

    _onCardClick(card) {
      const option1 = card.dataset.option1;

      // Already active — nothing to do
      if (card.classList.contains('is-selected')) return;

      // Update card states
      this.querySelectorAll('.svp__card').forEach((c) => {
        const active = c.dataset.option1 === option1;
        c.classList.toggle('is-selected', active);
        c.setAttribute('aria-pressed', active);
      });

      // Update dynamic title value
      const titleValue = this.querySelector('.svp__opt1-value');
      if (titleValue) titleValue.textContent = ' ' + option1;

      // Handle option 2
      if (this._hasOpt2) {
        const opt2Section = this.querySelector('.svp__opt2');
        const allBtns     = [...this.querySelectorAll('.svp__opt2-btn')];
        const validBtns   = allBtns.filter((b) => b.dataset.option1 === option1);

        // Show/hide individual buttons
        allBtns.forEach((b) => { b.hidden = (b.dataset.option1 !== option1); });

        // Show/hide the whole section
        if (opt2Section) opt2Section.hidden = (validBtns.length === 0);

        // Auto-select first valid option2 → resolves the variant
        if (validBtns.length > 0) {
          allBtns.forEach((b) => { b.classList.remove('is-selected'); b.setAttribute('aria-pressed', 'false'); });
          validBtns[0].classList.add('is-selected');
          validBtns[0].setAttribute('aria-pressed', 'true');
          this._applyVariant(parseInt(validBtns[0].dataset.variantId, 10));
          return;
        }
      }

      // No option2 — find best variant by option1 alone
      const variant =
        this._variants.find((v) => v.option1 === option1 && v.available) ||
        this._variants.find((v) => v.option1 === option1);
      if (variant) this._applyVariant(variant.id);
    }

    // ── Option 2 button click ──────────────────────────────────────────────

    _onOpt2Click(btn) {
      const variantId = parseInt(btn.dataset.variantId, 10);
      const option1   = btn.dataset.option1;

      // Update button states (only within same option1 group)
      this.querySelectorAll('.svp__opt2-btn').forEach((b) => {
        if (b.dataset.option1 === option1) {
          const active = parseInt(b.dataset.variantId, 10) === variantId;
          b.classList.toggle('is-selected', active);
          b.setAttribute('aria-pressed', active);
        }
      });

      this._applyVariant(variantId);
    }

    // ── Core: resolve variant & propagate ─────────────────────────────────

    _applyVariant(variantId) {
      const variant = this._variants.find((v) => v.id === variantId);
      if (!variant) return;

      // Update selected card price
      this._updateCardPrice(variant);

      // Update description section
      this._updateDescription(variantId);

      // Update hidden form input (same pattern as custom-variant-picker)
      this._updateVariantInput(variantId);

      // Update product image carousel
      if (variant.featured_media_id) {
        this._updateMedia(variant.featured_media_id);
      }

      // Enable / disable add-to-cart button
      this._toggleAddButton(!variant.available);

      // Fetch server-rendered price / inventory
      this._fetchVariantSections(variantId);

      // Notify rest of page (sticky bar, accessories, etc.)
      if (typeof dispatchCustomEvent === 'function') {
        dispatchCustomEvent('product:variant-change', {
          variant: { id: variant.id, available: variant.available },
          sectionId: this._sectionId,
        });
      }

      // Update URL without reload
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }

    // ── Card price update ─────────────────────────────────────────────────

    _updateCardPrice(variant) {
      const activeCard = this.querySelector(`.svp__card[data-option1="${CSS.escape(variant.option1)}"]`);
      if (!activeCard || !variant.price_formatted) return;
      const priceEl = activeCard.querySelector('.svp__card-price');
      if (priceEl) priceEl.textContent = variant.price_formatted;
    }

    // ── Description toggle ────────────────────────────────────────────────

    _updateDescription(variantId) {
      this.querySelectorAll('.svp__description').forEach((d) => {
        d.hidden = parseInt(d.dataset.variantId, 10) !== variantId;
      });
    }

    // ── Form input (mirrors custom-variant-picker.js exactly) ─────────────

    _updateVariantInput(variantId) {
      document
        .querySelectorAll(`#product-form-${this._sectionId}, #product-form-installment`)
        .forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
    }

    // ── Media / carousel (mirrors custom-variant-picker.js exactly) ────────

    _updateMedia(mediaId) {
      if (!this._productSlider) return;

      const mediaSelector = `#Slide-${this._sectionId}-${mediaId}`;
      const thumbSelector = `#Thumb-${this._sectionId}-${mediaId}`;

      if (this._thumbnails) {
        if (typeof Flickity !== 'undefined') {
          const flkty = Flickity.data(this._productSlider);
          if (flkty) this._productSlider.selectCell(mediaSelector);
        }
        const activeThumb = this._thumbnails.querySelector(thumbSelector);
        if (activeThumb) {
          this._thumbnails.querySelectorAll('.product-thumbnail').forEach((t) =>
            t.classList.remove('is-active', 'is-initial-selected')
          );
          activeThumb.classList.add('is-active', 'is-initial-selected');
        }
      } else {
        const activeMedia = this._productSlider.querySelector(mediaSelector);
        if (activeMedia) {
          this._productSlider.querySelectorAll('[data-media-id]').forEach((el) =>
            el.classList.remove('is-active')
          );
          activeMedia.classList.add('is-active');
          activeMedia.parentElement.prepend(activeMedia);
        }
      }
    }

    // ── Add-to-cart button (mirrors custom-variant-picker.js exactly) ──────

    _toggleAddButton(disable) {
      document.querySelectorAll('.single-add-to-cart-button').forEach((btn) => {
        const textEl = btn.querySelector('.single-add-to-cart-button--text');
        if (disable) {
          btn.setAttribute('disabled', 'disabled');
          if (textEl) textEl.textContent = window.theme?.variantStrings?.soldOut || 'Sold out';
        } else {
          btn.removeAttribute('disabled');
          btn.classList.remove('loading');
          if (textEl) textEl.textContent = window.theme?.variantStrings?.addToCart || 'Add to cart';
        }
      });
    }

    // ── Section rendering fetch (mirrors custom-variant-picker.js exactly) ─

    _fetchVariantSections(variantId) {
      const url = `${this._productUrl}?variant=${variantId}&section_id=${this._sectionId}`;

      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          [
            `price-${this._sectionId}`,
            `price-${this._sectionId}--sticky`,
            `inventory-${this._sectionId}`,
            `sku-${this._sectionId}`,
            `quantity-${this._sectionId}`,
            `product-image-${this._sectionId}--sticky`,
          ].forEach((id) => {
            const dest = document.getElementById(id);
            const src  = doc.getElementById(id);
            if (dest && src) {
              dest.innerHTML = src.innerHTML;
              if (id.includes('price')) dest.classList.remove('visibility-hidden');
            }
          });
        })
        .catch(() => {});
    }
  }

  customElements.define('simple-variant-picker', SimpleVariantPicker);
}
